import { useState, useEffect, useCallback, useMemo } from 'react';
import { Pagination } from 'react-bootstrap';
import { Spinner, Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import FeatherIcon from 'feather-icons-react';

import { getVoiceSessions, getVoiceSessionTranscript } from './FormikHelpers/Api';
import { SESSION_STATUS_COLORS, formatDate, formatDuration } from './FormikHelpers/InitialValues';

const PER_PAGE = 15;

// ─── TranscriptModal ──────────────────────────────────────────────────────────

const TranscriptModal = ({ open, onClose, session }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !session) return;
    setData(null);
    setLoading(true);
    getVoiceSessionTranscript(session._id)
      .then((res) => setData(res?.data || null))
      .catch(() => toast.error('Failed to load transcript'))
      .finally(() => setLoading(false));
  }, [open, session]);

  const turns = data?.conversation_history || [];

  return (
    <Modal isOpen={open} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FeatherIcon icon="phone-call" size={18} color="#1D4ED8" />
          <span>Transcript — {session?.caller_number || '—'}</span>
        </div>
      </ModalHeader>
      <ModalBody style={{ maxHeight: '65vh', overflowY: 'auto', padding: '16px 20px' }}>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spinner style={{ color: '#1D4ED8' }} />
          </div>
        )}

        {!loading && turns.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>
            <FeatherIcon icon="file-text" size={28} color="#CBD5E1" />
            <div style={{ marginTop: 8, fontSize: 13 }}>No transcript available for this session</div>
          </div>
        )}

        {!loading && turns.map((turn, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: turn.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 12,
            }}
          >
            <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 3 }}>
              {turn.role === 'assistant' ? '🤖 Assistant' : '👤 Caller'}
              {turn.latency_ms && (
                <span style={{ marginLeft: 8, color: '#CBD5E1' }}>{turn.latency_ms}ms</span>
              )}
            </div>
            <div
              style={{
                maxWidth: '75%', padding: '9px 13px', borderRadius: 10, fontSize: 13, lineHeight: 1.55,
                background: turn.role === 'assistant' ? '#EFF6FF' : '#F8FAFC',
                border: `1px solid ${turn.role === 'assistant' ? '#BFDBFE' : '#E2E8F0'}`,
                color: '#0F172A',
              }}
            >
              {turn.content}
            </div>
          </div>
        ))}
      </ModalBody>
      <div style={{ padding: '12px 20px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end' }}>
        <Button outline color="secondary" onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
};

// ─── VoiceSessionsTable ───────────────────────────────────────────────────────

const VoiceSessionsTable = ({ configId, configs = [] }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [convertedFilter, setConvertedFilter] = useState('');
  const [transcriptSession, setTranscriptSession] = useState(null);

  // ─── Fetch ───────────────────────────────────────────────────────────────

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (configId) params.config_id = configId;
      if (statusFilter) params.status = statusFilter;
      if (convertedFilter) params.converted = convertedFilter;
      const res = await getVoiceSessions(params);
      setSessions(res?.data || []);
      setCurrentPage(1);
    } catch {
      toast.error('Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  }, [configId, statusFilter, convertedFilter]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // ─── Pagination ──────────────────────────────────────────────────────────

  const paged = useMemo(() => sessions.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE), [sessions, currentPage]);
  const totalPages = Math.ceil(sessions.length / PER_PAGE);

  // ─── Config name ─────────────────────────────────────────────────────────

  const configName = (cfgId) => {
    const c = configs.find((x) => x._id === cfgId || x._id?.toString() === cfgId?.toString());
    return c?.persona?.assistant_name || c?.voice_slug || '—';
  };

  const selectStyle = {
    padding: '6px 10px', borderRadius: 8,
    border: '1px solid rgba(147,197,253,0.2)',
    background: 'rgba(255,255,255,0.06)', color: '#93C5FD',
    fontSize: 12, cursor: 'pointer', outline: 'none',
  };

  return (
    <>
      <Paper elevation={0} style={{ borderRadius: 10, border: '1px solid #E2E8F0', boxShadow: '0 4px 24px rgba(15,28,46,0.08)', overflow: 'hidden' }}>

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="ri-header" style={{ padding: '13px 20px' }}>
          <div className="ri-header__left">
            <div className="ri-header__icon-wrap">
              <FeatherIcon icon="phone" size={16} color="#93C5FD" />
            </div>
            <div className="ri-header__meta">
              <div className="ri-header__label">Voice Assistant</div>
              <div className="ri-header__title">Call Sessions</div>
            </div>
            <span className="ri-header__badge ri-header__badge--records">
              {sessions.length} total
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
              <option value="">All Statuses</option>
              {['completed', 'failed', 'in_progress', 'no_answer', 'busy', 'canceled'].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select value={convertedFilter} onChange={(e) => setConvertedFilter(e.target.value)} style={selectStyle}>
              <option value="">All Calls</option>
              <option value="true">Converted</option>
              <option value="false">Not Converted</option>
            </select>
          </div>
        </div>

        {/* ── Table ─────────────────────────────────────────────────────── */}
        <Box sx={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {['Caller', 'Assistant', 'Direction', 'Started', 'Duration', 'Turns', 'Status', 'Converted', ''].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '10px 14px', textAlign: 'left',
                      background: '#F8FAFC', fontWeight: 700, fontSize: 10.5,
                      color: '#64748B', borderBottom: '1px solid #E2E8F0',
                      whiteSpace: 'nowrap', letterSpacing: '0.4px',
                    }}
                  >{h}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, gap: 12, color: '#64748B' }}>
                      <Spinner style={{ color: '#1D4ED8' }} /><span>Loading sessions…</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && paged.length === 0 && (
                <tr>
                  <td colSpan={9}>
                    <div style={{ padding: 48, textAlign: 'center' }}>
                      <FeatherIcon icon="phone-off" size={28} color="#CBD5E1" />
                      <Typography style={{ fontWeight: 700, color: '#334155', fontSize: 14, marginTop: 10 }}>No Sessions Yet</Typography>
                      <Typography style={{ color: '#94A3B8', fontSize: 12, marginTop: 4 }}>
                        Call sessions will appear here once your assistant starts receiving calls.
                      </Typography>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && paged.map((session, idx) => {
                const stColor = SESSION_STATUS_COLORS[session.status] || '#94A3B8';
                return (
                  <tr key={session._id} style={{ background: idx % 2 === 0 ? '#fff' : '#F8FAFC', borderBottom: '1px solid #F1F5F9' }}>

                    <td style={{ padding: '10px 14px', fontWeight: 600, color: '#0F172A', whiteSpace: 'nowrap' }}>
                      {session.caller_number || '—'}
                    </td>

                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748B', whiteSpace: 'nowrap' }}>
                      {configName(session.voice_config_id)}
                    </td>

                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                        background: session.direction === 'inbound' ? '#EFF6FF' : '#F5F3FF',
                        color: session.direction === 'inbound' ? '#1D4ED8' : '#7C3AED',
                        border: `1px solid ${session.direction === 'inbound' ? '#BFDBFE' : '#DDD6FE'}`,
                      }}>
                        {session.direction || 'inbound'}
                      </span>
                    </td>

                    <td style={{ padding: '10px 14px', fontSize: 11.5, color: '#64748B', whiteSpace: 'nowrap' }}>
                      {formatDate(session.started_at || session.createdAt)}
                    </td>

                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748B', whiteSpace: 'nowrap' }}>
                      {formatDuration(session.duration_seconds)}
                    </td>

                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748B', textAlign: 'center' }}>
                      {session.total_turns}
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: stColor }}>
                        {session.status}
                      </span>
                    </td>

                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      {session.converted
                        ? (
                          <Tooltip title="Lead was captured" arrow>
                            <span><FeatherIcon icon="user-check" size={14} color="#059669" /></span>
                          </Tooltip>
                        ) : (
                          <span style={{ color: '#CBD5E1', fontSize: 11 }}>—</span>
                        )}
                    </td>

                    <td style={{ padding: '10px 14px' }}>
                      <button
                        type="button"
                        onClick={() => setTranscriptSession(session)}
                        style={{
                          padding: '3px 9px', borderRadius: 6,
                          border: '1.5px solid #BFDBFE', background: '#EFF6FF',
                          fontSize: 11, fontWeight: 600, color: '#1D4ED8',
                          cursor: 'pointer', whiteSpace: 'nowrap',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <FeatherIcon icon="file-text" size={11} /> Transcript
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>

        {/* ── Pagination ────────────────────────────────────────────────── */}
        {totalPages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination size="sm" className="mb-0">
              <Pagination.Prev onClick={() => currentPage > 1 && setCurrentPage((p) => p - 1)} disabled={currentPage === 1} />
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next onClick={() => currentPage < totalPages && setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages} />
            </Pagination>
          </div>
        )}
      </Paper>

      <TranscriptModal
        open={!!transcriptSession}
        onClose={() => setTranscriptSession(null)}
        session={transcriptSession}
      />
    </>
  );
};

export { VoiceSessionsTable };